import { HotFilterItem } from '@interfaces/api/responses/hot-filters.response';

type Param = Record<string, string>;

// `Women#1`, `Men#2`, `Kids#3` under `universe` will result in: `universe=Women#1_Men#2_Kids#3`.
const getGroupedByHotFilterTabsParentKey = (tabs?: HotFilterItem[]): Record<string, string[]> => {
  return tabs.reduce((acc, tab) => {
    if (!acc[tab.parentKey]) {
      acc[tab.parentKey] = [];
    }
    acc[tab.parentKey].push(`${tab.name}#${tab.filterId}`);
    return acc;
  }, {});
};

// Converts `{ "universe": ["Women#1", "Men#2", "Kids#3"] }` to `"universe=Women#1_Men#2_Kids#3"`.
const createResultStringsFromHotFilterTabsGroup = (groupedByParentKey: Record<string, string[]>): string => {
  return Object.entries(groupedByParentKey)
    .map(([parentKey, entries]) => `${parentKey}=${entries.join('_')}`)
    .join('_');
};

/**
 * Updates URL parameters based on hot filters and tab data.
 * Param will be updated to { 'universe': 'Universe 1#filter123', 'category': 'someCategory' }
 * If no matching tabs are found, the 'universe' key will be cleared from `param`.
 *
 * getGroupedByHotFilterTabsParentKey, createResultStringsFromHotFilterTabsGroup will result in:
 * `Women#1`, `Men#2`, under `universe` to `universe=Women#1_Men#2`.
 *
 */
export const updateParamsWithHotFiltersTabs = (
  param: Param,
  tabs: HotFilterItem,
  matchingTabs: HotFilterItem[],
): void => {
  let resultStrings = '';

  if (matchingTabs.length > 1) {
    const groupedByParentKey = getGroupedByHotFilterTabsParentKey(matchingTabs);
    resultStrings = createResultStringsFromHotFilterTabsGroup(groupedByParentKey);
  } else if (matchingTabs.length === 1) {
    const matchedTab = matchingTabs[0];
    resultStrings = `${matchedTab.parentKey}=${matchedTab.name}#${matchedTab.filterId}`;
  }

  if (resultStrings) {
    const [propertyName, propertyValue] = resultStrings.split('=');
    param[propertyName] = propertyValue;
  } else {
    // Remove universe key if category is empty
    const checkedPropertyNames = Object.keys(
      tabs.children.reduce((acc, tab) => {
        acc[tab.parentKey] = true;
        return acc;
      }, {}),
    );
    checkedPropertyNames.forEach((propertyName) => (param[propertyName] = ''));
  }
};

// Constructs a result string for checked filter items with their hierarchy. "Clothing#1>Shirts#2#1_Suits#3#2"
export const generateHotFiltersCheckedItemsUrlFragment = (items: HotFilterItem[]): string => {
  const itemsString = items
    .filter((item) => item.isChecked && !item.id.toString().startsWith('all_'))
    .map((item) => {
      // Construct the base string with item.name and item.id
      let baseString = `${item.name}#${item.id}`;

      // If parentID exists, include it in the format
      if (item.parentID) {
        baseString = `${baseString}^${item.parentID}`;
      }
      const childrenString =
        item.children && item.children.length > 0 ? `>${generateHotFiltersCheckedItemsUrlFragment(item.children)}` : '';

      return `${baseString}${childrenString}`;
    })
    .join('_');

  return itemsString;
};

export const generateHotFiltersCheckedItemsUrlFragmentCategory = (
  items: HotFilterItem[],
  depth: number = 0,
): string => {
  if (!items || items.length === 0) {
    return '';
  }

  // "Clothing#2^1>Coats#20^2_Dresses#15^2_Skirts#17^2_Swimwear#502^2__Accessories#10^3>Bags & Pencil cases#92^10_Hats & Gloves#88^10"
  const separator = depth === 0 ? '__' : '_'; // Determine the separator based on depth

  const itemsString = items
    .filter((item) => item.isChecked && !item.id.toString().startsWith('all_'))
    .map((item) => {
      // Construct the base string with item.name and item.id
      let baseString = `${item.name}#${item.id}`;

      // If parentID exists, include it in the format
      if (item.parentID) {
        baseString = `${baseString}^${item.parentID}`;
      }

      const childrenString =
        item.children && item.children.length > 0
          ? `>${generateHotFiltersCheckedItemsUrlFragmentCategory(item.children, depth + 1)}`
          : '';

      // Return the combined string with the appropriate separator
      return `${baseString}${childrenString}`;
    })
    .join(separator);

  return itemsString;
};
